import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faToggleOn, faToggleOff } from '@fortawesome/free-solid-svg-icons';

interface Particle {
  x: number;
  y: number;
  dx: number;
  dy: number;
  size: number;
  color: string;
  life: number;
}

const Canvas = styled.canvas<{}>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 100;
`;

const ToggleContainer = styled(motion.div)`
  position: fixed;
  left: 0;
  top: 80%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 0;
  z-index: 1000;

  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    left: 0;
    top: unset;
    bottom: 1.7rem;
    transform: none;
  }
`;

const ToggleButton = styled(motion.button)<{ $isEnabled: boolean }>`
  background: ${(props) =>
    props.$isEnabled ? props.theme.colors.secondary : '#9e9e9e'};
  color: ${(props) => props.theme.colors.white};
  width: 50px;
  height: 50px;
  border-radius: 0 50% 50% 0;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;

  &:hover {
    background: ${(props) =>
      props.$isEnabled ? props.theme.colors.secondary : '#757575'};
    transform: scale(1.07);
  }

  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    width: 45px;
    height: 45px;
    font-size: 1rem;
  }
`;

const ToggleText = styled(motion.span)`
  background: rgba(255, 255, 255, 0.9);
  padding: 0.7rem 1.1em 0.7rem 1.9rem;
  font-weight: 500;
  color: ${(props) => props.theme.colors.secondary};
  font-weight: 600;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.32);
  white-space: nowrap;
  margin-left: -20px;
  z-index: -1;

  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    font-size: 0.9rem;
    padding: 0.4rem 0.8rem 0.4rem 1.6rem;
  }
`;

const ParticleSystem: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const particlesRef = useRef<Particle[]>([]);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isTouching, setIsTouching] = useState(false);

  const colors = ['#FFD700', '#800080', '#FF69B4', '#4B0082'];

  useEffect(() => {
    if (!isEnabled) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      particlesRef.current = [];
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
      }
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const createParticles = (x: number, y: number, count: number) => {
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 2 + 1;
        particlesRef.current.push({
          x,
          y,
          dx: Math.cos(angle) * speed,
          dy: Math.sin(angle) * speed,
          size: Math.random() * 4 + 2,
          color: colors[Math.floor(Math.random() * colors.length)],
          life: 1,
        });
      }
    };

    const handleInteraction = (x: number, y: number) => {
      if (!isEnabled) return;
      setMousePosition({ x, y });
      createParticles(x, y, 2);
    };

    const handleMouseMove = (e: MouseEvent) => {
      handleInteraction(e.clientX, e.clientY);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isTouching) return;
      const touch = e.touches[0];
      // Trigger particle effect only if touch is in the top half of the screen
      if (touch.clientY < window.innerHeight / 2) {
        handleInteraction(touch.clientX, touch.clientY);
      }
    };

    const handleTouchStart = (e: TouchEvent) => {
      setIsTouching(true);
      const touch = e.touches[0];
      handleInteraction(touch.clientX, touch.clientY);
    };

    const handleTouchEnd = () => {
      setIsTouching(false);
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update and draw particles
      particlesRef.current = particlesRef.current.filter((particle) => {
        particle.x += particle.dx;
        particle.y += particle.dy;
        particle.life -= 0.02;

        if (particle.life <= 0) return false;

        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle =
          particle.color +
          Math.floor(particle.life * 255)
            .toString(16)
            .padStart(2, '0');
        ctx.fill();

        // Add connecting lines between nearby particles
        particlesRef.current.forEach((otherParticle) => {
          const dx = particle.x - otherParticle.x;
          const dy = particle.y - otherParticle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 100) {
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(otherParticle.x, otherParticle.y);
            ctx.strokeStyle = particle.color + '40';
            ctx.stroke();
          }
        });

        return true;
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchstart', handleTouchStart);
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);
    animate();

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [colors, isEnabled, isTouching]);

  return (
    <>
      <AnimatePresence>
        <ToggleContainer
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <ToggleButton
            onClick={() => setIsEnabled(!isEnabled)}
            whileTap={{ scale: 0.9 }}
            $isEnabled={isEnabled}
            title={isEnabled ? 'Disable particles' : 'Enable particles'}
          >
            <FontAwesomeIcon icon={isEnabled ? faToggleOn : faToggleOff} />
          </ToggleButton>
          <ToggleText
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            {isEnabled ? 'Enough with craziness!' : 'Wanna see some fun!?'}
          </ToggleText>
        </ToggleContainer>
      </AnimatePresence>
      <Canvas ref={canvasRef} />
    </>
  );
};

export default ParticleSystem;
